import React from 'react';
import { HiOfficeBuilding, HiLocationMarker } from 'react-icons/hi';
import { FaBriefcase, FaChalkboardTeacher, FaGraduationCap } from 'react-icons/fa';

const MONTHS = { Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5, Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11 };

function parseDate(str) {
  if (str === 'Present') return new Date();
  const [m, y] = str.split(' ');
  return new Date(parseInt(y), MONTHS[m], 1);
}

function computeDuration(from, to) {
  const diff = (parseDate(to).getFullYear() - parseDate(from).getFullYear()) * 12
             + (parseDate(to).getMonth() - parseDate(from).getMonth());
  const inclusive = diff + 1;
  const yrs = Math.floor(inclusive / 12);
  const mos = inclusive % 12;
  if (yrs === 0) return `${mos} mos`;
  if (mos === 0) return `${yrs} yr${yrs > 1 ? 's' : ''}`;
  return `${yrs} yr${yrs > 1 ? 's' : ''} ${mos} mos`;
}

function computeTotalDuration(roles) {
  if (!roles.length) return '0 mos';
  let minDate = parseDate(roles[0].from);
  let maxDate = parseDate(roles[0].to);
  for (const r of roles) {
    const fd = parseDate(r.from), td = parseDate(r.to);
    if (fd < minDate) minDate = fd;
    if (td > maxDate) maxDate = td;
  }
  const diff = (maxDate.getFullYear() - minDate.getFullYear()) * 12
             + (maxDate.getMonth() - minDate.getMonth());
  const inclusive = diff + 1;
  const yrs = Math.floor(inclusive / 12);
  const mos = inclusive % 12;
  if (yrs === 0) return `${mos} mos`;
  if (mos === 0) return `${yrs} yr${yrs > 1 ? 's' : ''}`;
  return `${yrs} yr${yrs > 1 ? 's' : ''} ${mos} mos`;
}

const experiences = [
  {
    id: 1,
    company: 'Brain Station 23 PLC',
    type: 'Full-time',
    location: 'Dhaka, Bangladesh',
    Icon: HiOfficeBuilding,
    accent: 'border-indigo-500',
    dotBg: 'bg-indigo-50 border-indigo-300',
    iconColor: 'text-indigo-500',
    durationBg: 'bg-indigo-50 text-indigo-600',
    roles: [
      { title: 'Senior Software Engineer I',    from: 'Jan 2026', to: 'Present',  current: true  },
      { title: 'Software Engineer II',           from: 'Jul 2024', to: 'Dec 2025', current: false },
      { title: 'Associate Software Engineer II', from: 'Jul 2023', to: 'Jun 2024', current: false },
      { title: 'Associate Software Engineer I',  from: 'Nov 2022', to: 'Jun 2023', current: false },
      { title: 'Software Engineer Trainee',      from: 'Jul 2022', to: 'Oct 2022', current: false },
    ],
  },
  {
    id: 2,
    company: 'Fiverr',
    type: 'Freelance · Remote',
    location: 'Remote',
    Icon: FaBriefcase,
    accent: 'border-teal-500',
    dotBg: 'bg-teal-50 border-teal-300',
    iconColor: 'text-teal-500',
    durationBg: 'bg-teal-50 text-teal-600',
    roles: [
      { title: 'Full Stack Developer', from: 'Mar 2021', to: 'Jun 2022', current: false },
    ],
  },
  {
    id: 3,
    company: 'American International University-Bangladesh',
    type: 'Internship',
    location: 'Dhaka, Bangladesh',
    Icon: FaChalkboardTeacher,
    accent: 'border-purple-500',
    dotBg: 'bg-purple-50 border-purple-300',
    iconColor: 'text-purple-500',
    durationBg: 'bg-purple-50 text-purple-600',
    roles: [
      { title: 'Teaching Assistant', from: 'Jun 2021', to: 'Aug 2021', current: false },
    ],
  },
];

function Experience() {
  return (
    <div id="experience" className="bg-white py-16 px-4 sm:px-12 md:px-24">

      <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
        Work <span className="text-indigo-600">Experience</span>
        <br />
        <span className="w-8 h-1 bg-indigo-500 inline-block rounded-full mt-2" />
      </h2>

      <div className="max-w-3xl mx-auto relative">

        {/* Vertical line */}
        <div className="absolute left-5 top-2 bottom-2 w-0.5 bg-gray-200" />

        <div className="flex flex-col gap-8">
          {experiences.map(exp => (
            <div
              key={exp.id}
              className="relative pl-16"
              data-aos="fade-up"
              data-aos-duration="1500"
            >
              {/* Timeline dot */}
              <div className={`absolute left-0 w-10 h-10 rounded-full border-2 flex items-center justify-center ${exp.dotBg}`}>
                <exp.Icon className={`w-4 h-4 ${exp.iconColor}`} />
              </div>

              {/* Card with left accent border */}
              <div className={`bg-white border-l-4 ${exp.accent} border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-300 rounded-xl p-5`}>

                {/* Company header */}
                <div className="flex items-start justify-between flex-wrap gap-2 mb-4">
                  <div>
                    <h3 className="text-base font-bold text-gray-900">{exp.company}</h3>
                    <p className="text-xs text-gray-400 font-medium mt-0.5 flex items-center gap-1">
                      <HiLocationMarker className="w-3 h-3" />
                      {exp.type} · {exp.location}
                    </p>
                  </div>
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${exp.durationBg}`}>
                    {computeTotalDuration(exp.roles)}
                  </span>
                </div>

                {/* Roles */}
                <div className={`flex flex-col gap-3 ${exp.roles.length > 1 ? 'border-l-2 border-gray-100 pl-4 ml-1' : ''}`}>
                  {exp.roles.map((role, i) => (
                    <div key={i} className="relative">
                      {exp.roles.length > 1 && (
                        <div className={`absolute -left-5 top-1.5 w-2 h-2 rounded-full ${role.current ? 'bg-green-400' : 'bg-gray-300'}`} />
                      )}
                      <div className="flex items-center justify-between flex-wrap gap-1">
                        <p className={`text-sm font-semibold ${role.current ? 'text-gray-900' : 'text-gray-700'}`}>
                          {role.title}
                        </p>
                        <div className="flex items-center gap-2">
                          {role.current && (
                            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-50 text-green-600 flex items-center gap-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" /> Active
                            </span>
                          )}
                          <span className={`text-xs font-medium ${role.current ? 'text-indigo-500' : 'text-gray-400'}`}>
                            {computeDuration(role.from, role.to)}
                          </span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">{role.from} – {role.to}</p>
                    </div>
                  ))}
                </div>

              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Education */}
      <div className="max-w-3xl mx-auto mt-16 pt-10 border-t border-gray-100">
        <h3 className="text-xl font-bold text-gray-800 mb-8 flex items-center gap-2">
          <FaGraduationCap className="text-green-500" /> Education
        </h3>
        <div className="relative pl-16" data-aos="fade-up" data-aos-duration="1500">
          <div className="absolute left-0 w-10 h-10 rounded-full bg-green-50 border-2 border-green-300 flex items-center justify-center">
            <FaGraduationCap className="w-4 h-4 text-green-500" />
          </div>
          <div className="bg-white border-l-4 border-green-500 border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-300 rounded-xl p-5">
            <div className="flex items-start justify-between flex-wrap gap-2 mb-3">
              <div>
                <h4 className="text-base font-bold text-gray-900">American International University-Bangladesh</h4>
                <p className="text-xs text-gray-400 font-medium mt-0.5 flex items-center gap-1">
                  <HiLocationMarker className="w-3 h-3" /> Dhaka, Bangladesh
                </p>
              </div>
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-green-50 text-green-600">2018 – 2021</span>
            </div>
            <p className="text-sm font-semibold text-gray-700 mb-3">BSc in Computer Science and Software Engineering</p>
            <div className="flex flex-wrap gap-2">
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-600">CGPA 3.83 / 4.00</span>
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-yellow-50 text-yellow-600">★ 5× Dean's List</span>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}

export default Experience;
